// src/pages/finance-portal/bank-reconciliation/ReconciliationArchive.js
//
// Reconciliation archive (immutable historical record).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Skeleton, Stack, Table,
    TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export default function ReconciliationArchive() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detailDialog, setDetailDialog] = useState({ open: false, archive: null, payload: null, loading: false });

    const fetchAll = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/reconciliations/archives', {
                params: { page: page + 1, per_page: perPage },
            });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load archive.');
        } finally {
            setIsLoading(false);
        }
    }, [page, perPage]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const openDetail = async (a) => {
        setDetailDialog({ open: true, archive: a, payload: null, loading: true });
        try {
            const res = await axiosInstance.get(`/api/finance/reconciliations/archives/${a.id}`);
            setDetailDialog({ open: true, archive: a, payload: res.data?.payload, loading: false });
        } catch (err) {
            setDetailDialog({ open: true, archive: a, payload: null, loading: false });
            setError(err.response?.data?.message || 'Failed to load archive detail.');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={() => navigate('/finance-portal/bank-reconciliation')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                                Reconciliation Archive
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                Immutable, fingerprinted records — 7-year retention.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={300} /></Box>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                        <TableCell>Archived</TableCell>
                                        <TableCell>Reconciliation</TableCell>
                                        <TableCell>SHA-256 Fingerprint</TableCell>
                                        <TableCell>Retention Until</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                                No archived reconciliations yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map((a) => (
                                        <TableRow key={a.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>
                                                {new Date(a.archived_at).toLocaleString()}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <span>#{a.reconciliation_id}</span>
                                                    <Chip
                                                        icon={<VerifiedIcon sx={{ fontSize: '14px !important' }} />}
                                                        label="Sealed"
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.7rem', bgcolor: '#dcfce7', color: '#166534' }}
                                                    />
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.7rem', color: '#475569', fontFamily: 'monospace' }}>
                                                {a.payload_sha256?.slice(0, 16)}…{a.payload_sha256?.slice(-8)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{a.retention_until || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View payload">
                                                    <IconButton size="small" onClick={() => openDetail(a)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {a.pdf_url && (
                                                    <Tooltip title="Open PDF">
                                                        <IconButton size="small" component="a" href={a.pdf_url} target="_blank" rel="noopener noreferrer">
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                sx={{ '& .MuiTablePagination-toolbar': { fontFamily } }}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, archive: null, payload: null, loading: false })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Archive #{detailDialog.archive?.id}
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, mt: 0.5 }}>
                        SHA-256: {detailDialog.archive?.payload_sha256}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {detailDialog.loading ? (
                        <Skeleton variant="rounded" height={300} />
                    ) : detailDialog.payload ? (
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, mb: 1 }}>Header</Typography>
                            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '8px', mb: 2, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(detailDialog.payload.reconciliation, null, 2)}
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, mb: 1 }}>
                                Matched Lines ({(detailDialog.payload.matched_lines || []).length})
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 700, mb: 1 }}>
                                Bank-Only ({(detailDialog.payload.bank_only_lines || []).length}) • GL-Only ({(detailDialog.payload.gl_only_lines || []).length})
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 700, mb: 1 }}>
                                Exceptions ({(detailDialog.payload.exceptions || []).length})
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>Payload unavailable.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, archive: null, payload: null, loading: false })} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
